import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Frame, Menu, LogOut, User as UserIcon, Sparkles, CreditCard, Inbox } from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "../ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "../ui/dropdown-menu";
import { Badge } from "../ui/badge";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/calculator", label: "Calculator" },
  { to: "/framing-tips", label: "Framing Tips" },
  { to: "/mat-cutting-tips", label: "Mat Cutting" },
  { to: "/aesthetics", label: "Aesthetics" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

const accessBadge = { trial: "Trial", expired: "Trial ended", subscribed: "Subscribed" };

export function SiteHeader() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isActive = (to) => location.pathname === to;

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-white/85 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Frame className="h-5 w-5" />
          </div>
          <span className="font-heading text-lg font-extrabold leading-none tracking-tight">Framer's Bench</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(n.to) ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="account-menu-trigger" className="gap-2">
                  <UserIcon className="h-4 w-4" />
                  <span className="hidden max-w-[120px] truncate sm:inline">{user.name || user.email}</span>
                  <Badge variant={user.access === "subscribed" ? "default" : "secondary"} className="ml-1">
                    {user.access === "trial" && user.trial_days_left > 0
                      ? `Trial · ${user.trial_days_left}d`
                      : accessBadge[user.access] || "Trial"}
                  </Badge>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/calculator")} data-testid="account-calculator">Calculator</DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin/messages")} data-testid="account-admin-messages">
                    <Inbox className="mr-2 h-4 w-4" /> Contact messages
                  </DropdownMenuItem>
                )}
                {user.access === "subscribed" ? (
                  <DropdownMenuItem onClick={() => navigate("/account")} data-testid="account-manage-sub">
                    <CreditCard className="mr-2 h-4 w-4" /> Manage subscription
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="account-upgrade">
                    <Sparkles className="mr-2 h-4 w-4 text-primary" /> {user.access === "expired" ? "Subscribe" : "Upgrade"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="account-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" size="sm" className="hidden sm:inline-flex" data-testid="header-login" onClick={() => navigate("/login")}>Log in</Button>
              <Button size="sm" data-testid="header-start" onClick={() => navigate("/calculator")}>Start free</Button>
            </>
          )}

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-8 flex flex-col gap-1">
                {NAV.map((n) => (
                  <SheetClose asChild key={n.to}>
                    <Link to={n.to} data-testid={`mobile-nav-${n.to.slice(1)}`}
                      className="rounded-md px-3 py-3 text-base font-medium text-foreground hover:bg-secondary">
                      {n.label}
                    </Link>
                  </SheetClose>
                ))}
                <div className="my-2 h-px bg-border" />
                {!user && (
                  <SheetClose asChild>
                    <Link to="/login" data-testid="mobile-login" className="rounded-md px-3 py-3 text-base font-medium text-foreground hover:bg-secondary">Log in</Link>
                  </SheetClose>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
